import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UsageChart = ({ data, title = "Card Usage Trends" }) => {
  const [chartType, setChartType] = useState('bar');
  const [timeRange, setTimeRange] = useState('7d');

  const timeRanges = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  const getFilteredData = () => {
    // In a real app, this would filter based on timeRange
    return data;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface border border-border rounded-lg p-3 shadow-lg">
          <p className="text-text-primary font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
        <div className="flex items-center space-x-2">
          {/* Time Range Selector */}
          <div className="flex items-center space-x-1 bg-background rounded-lg p-1">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => setTimeRange(range.value)}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors duration-150 ${
                  timeRange === range.value
                    ? 'bg-accent text-accent-foreground'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
          
          {/* Chart Type Toggle */}
          <div className="flex items-center space-x-1 bg-background rounded-lg p-1">
            <button
              onClick={() => setChartType('bar')}
              className={`p-2 rounded transition-colors duration-150 ${
                chartType === 'bar' ?'bg-accent text-accent-foreground' :'text-text-muted hover:text-text-primary'
              }`}
            >
              <Icon name="BarChart3" size={16} />
            </button>
            <button
              onClick={() => setChartType('line')}
              className={`p-2 rounded transition-colors duration-150 ${
                chartType === 'line' ?'bg-accent text-accent-foreground' :'text-text-muted hover:text-text-primary'
              }`}
            >
              <Icon name="TrendingUp" size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart data={getFilteredData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="name" 
                stroke="#a3a3a3"
                fontSize={12}
              />
              <YAxis 
                stroke="#a3a3a3"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="cards" 
                fill="#3b82f6" 
                radius={[4, 4, 0, 0]}
                name="Active Cards"
              />
              <Bar 
                dataKey="payments" 
                fill="#10b981" 
                radius={[4, 4, 0, 0]}
                name="Payments"
              />
            </BarChart>
          ) : (
            <LineChart data={getFilteredData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="name" 
                stroke="#a3a3a3"
                fontSize={12}
              />
              <YAxis 
                stroke="#a3a3a3"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="cards" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                name="Active Cards"
              />
              <Line 
                type="monotone" 
                dataKey="payments" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                name="Payments"
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-accent rounded-full"></div>
            <span className="text-text-muted">Active Cards</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span className="text-text-muted">Payments</span>
          </div>
        </div>
        <Button
          variant="ghost"
          iconName="Download"
          iconPosition="left"
          className="text-sm"
        >
          Export Data
        </Button>
      </div>
    </div>
  );
};

export default UsageChart;